/**
 * Auth Module
 *
 * Handles all authentication flows for TryOn end-users:
 *  - Email / password  (register, login)
 *  - Firebase social   (Google, Apple via idToken)
 *  - Guest token       (anonymous, limited try-ons)
 *  - Token refresh     (called automatically by fetcher on 401)
 *  - Logout
 */

import type {
  AuthResponse,
  GuestTokenResponse,
  TryonUser,
  AuthTokens,
  TryStyleEvents,
} from '../types';
import type { Fetcher } from '../utils/fetcher';
import { storage, STORAGE_KEYS } from '../utils/storage';
import type { EventEmitter } from '../utils/emitter';

interface ApiAuthResponse {
  success: boolean;
  message: string;
  data: {
    user: TryonUser;
    accessToken: string;
    refreshToken?: string;
  };
}

interface ApiGuestResponse {
  success: boolean;
  message: string;
  data: GuestTokenResponse;
}

interface ApiRefreshResponse {
  success: boolean;
  data: { accessToken: string };
}

interface ApiMeResponse {
  success: boolean;
  data: TryonUser & { isGuest?: boolean };
}

export class AuthModule {
  constructor(
    private readonly fetcher: Fetcher,
    private readonly emitter: EventEmitter<TryStyleEvents>,
  ) {}

  // ─── State ─────────────────────────────────────────────────────────────────

  getAccessToken(): string | null {
    return storage.get(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return storage.get(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getCurrentUser(): TryonUser | null {
    const raw = storage.get(STORAGE_KEYS.USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as TryonUser;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private saveSession(data: ApiAuthResponse['data']): AuthResponse {
    storage.set(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    if (data.refreshToken) {
      storage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
    }
    const user = { ...data.user, isGuest: false };
    storage.set(STORAGE_KEYS.USER, JSON.stringify(user));

    this.emitter.emit('auth:login', {
      user,
      tokens: { accessToken: data.accessToken, refreshToken: data.refreshToken },
    });

    return { user, accessToken: data.accessToken, refreshToken: data.refreshToken };
  }

  private clearSession(): void {
    storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    storage.remove(STORAGE_KEYS.USER);
    this.emitter.emit('auth:logout', null);
  }

  // ─── Email / password ──────────────────────────────────────────────────────

  async register(email: string, password: string, name: string): Promise<AuthResponse> {
    const res = await this.fetcher.request<ApiAuthResponse>('/api/v1/auth/tryon/register', {
      method: 'POST',
      body: { email, password, name },
    });
    return this.saveSession(res.data);
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const res = await this.fetcher.request<ApiAuthResponse>('/api/v1/auth/tryon/login', {
      method: 'POST',
      body: { email, password },
    });
    return this.saveSession(res.data);
  }

  // ─── Firebase social auth ─────────────────────────────────────────────────

  /**
   * Exchange a Firebase ID token (from Google/Apple sign-in) for a TryStyle JWT.
   * @param idToken - Firebase ID token obtained from Firebase SDK or OAuth popup
   */
  async loginWithFirebase(idToken: string): Promise<AuthResponse> {
    const res = await this.fetcher.request<ApiAuthResponse>(
      '/api/v1/auth/tryon/firebase-signin',
      { method: 'POST', body: { idToken } },
    );
    return this.saveSession(res.data);
  }

  // ─── Guest ─────────────────────────────────────────────────────────────────

  async getGuestToken(): Promise<GuestTokenResponse> {
    const res = await this.fetcher.request<ApiGuestResponse>(
      '/api/v1/auth/tryon/guest/token',
      { method: 'POST' },
    );

    storage.set(STORAGE_KEYS.ACCESS_TOKEN, res.data.accessToken);
    const guestUser: TryonUser = {
      id: res.data.sessionId,
      email: null,
      name: 'Guest',
      authProvider: 'guest',
      emailVerified: false,
      photoUrl: null,
      profileImageUrl: null,
      tier: 'free',
      isGuest: true,
      createdAt: new Date().toISOString(),
      lastLoginAt: null,
    };
    storage.set(STORAGE_KEYS.USER, JSON.stringify(guestUser));
    this.emitter.emit('auth:login', { user: guestUser, tokens: { accessToken: res.data.accessToken } });

    return res.data;
  }

  // ─── Token refresh (called by fetcher on 401) ─────────────────────────────

  async refreshToken(): Promise<string | null> {
    // Guest users have no refresh token — silently get a fresh guest token
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      const user = this.getCurrentUser();
      if (user?.isGuest) {
        return this.silentRefreshGuestToken();
      }
      return null;
    }

    try {
      const res = await this.fetcher.request<ApiRefreshResponse>(
        '/api/v1/auth/tryon/refresh',
        { method: 'POST', body: { refreshToken }, _retry: true },
      );

      const newAccessToken = res.data.accessToken;
      storage.set(STORAGE_KEYS.ACCESS_TOKEN, newAccessToken);
      this.emitter.emit('auth:token-refreshed', { accessToken: newAccessToken });
      return newAccessToken;
    } catch {
      this.clearSession();
      return null;
    }
  }

  /** Silently renew a guest token without emitting login events */
  private async silentRefreshGuestToken(): Promise<string | null> {
    try {
      const res = await this.fetcher.request<ApiGuestResponse>(
        '/api/v1/auth/tryon/guest/token',
        { method: 'POST', _retry: true },
      );

      storage.set(STORAGE_KEYS.ACCESS_TOKEN, res.data.accessToken);
      return res.data.accessToken;
    } catch {
      this.clearSession();
      return null;
    }
  }

  // ─── Profile ──────────────────────────────────────────────────────────────

  async getProfile(): Promise<TryonUser> {
    const res = await this.fetcher.request<ApiMeResponse>('/api/v1/auth/tryon/me');
    const user = res.data;
    storage.set(STORAGE_KEYS.USER, JSON.stringify(user));
    return user;
  }

  // ─── Logout ───────────────────────────────────────────────────────────────

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    try {
      await this.fetcher.request('/api/v1/auth/tryon/logout', {
        method: 'POST',
        body: refreshToken ? { refreshToken } : {},
      });
    } catch { /* ignore — always clear local session */ }
    this.clearSession();
  }

  // ─── Password flows ────────────────────────────────────────────────────────

  async forgotPassword(email: string): Promise<void> {
    await this.fetcher.request('/api/v1/auth/tryon/forgot-password', {
      method: 'POST',
      body: { email },
    });
  }

  async verifyOtp(email: string, otp: string): Promise<{ resetToken: string }> {
    const res = await this.fetcher.request<{ success: boolean; data: { resetToken: string } }>(
      '/api/v1/auth/tryon/verify-otp',
      { method: 'POST', body: { email, otp } },
    );
    return res.data;
  }

  async resetPassword(resetToken: string, newPassword: string): Promise<void> {
    await this.fetcher.request('/api/v1/auth/tryon/reset-password', {
      method: 'POST',
      body: { resetToken, newPassword },
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.fetcher.request('/api/v1/auth/tryon/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  }
}
